// /apps/patient/src/pages/records/EncountersList.tsx

import { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Stethoscope, ClipboardList, FileText } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useFeatures } from '../../contexts/FeatureContext';
import { VisitsTab } from './VisitsTab';
import { OrdersTab } from './OrdersTab';
import { DocumentsTab } from './DocumentsTab';

type TabId = 'visits' | 'orders' | 'documents';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  feature?: string;
}

export function EncountersList() {
  const { hasFeature } = useFeatures();
  const [activeTab, setActiveTab] = useState<TabId>('visits');

  const tabs: Tab[] = [
    { id: 'visits', label: 'Visits', icon: <Stethoscope className="h-4 w-4" /> },
    { id: 'orders', label: 'Orders', icon: <ClipboardList className="h-4 w-4" />, feature: 'orders' },
    { id: 'documents', label: 'Documents', icon: <FileText className="h-4 w-4" />, feature: 'documents' },
  ];

  const visibleTabs = tabs.filter(tab => !tab.feature || hasFeature(tab.feature as any));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="My Health Records" />

      {/* Section Tabs */}
      {visibleTabs.length > 1 && (
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'visits' && <VisitsTab />}
      {activeTab === 'orders' && <OrdersTab />}
      {activeTab === 'documents' && <DocumentsTab />}
    </div>
  );
}
